package com.swetdot.swetgram.data.auth

import com.swetdot.swetgram.protocol.ProtocolJson
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Persisted credentials.
 *
 * `accessExpiresAtMs` is computed on receipt from the contract's `access_expires_in` (a TTL in
 * SECONDS, not an absolute time). Deriving it locally means a client with a wrong clock stays
 * wrong by the same fixed amount, rather than compounding its own error with the server's.
 */
@Serializable
data class StoredSession(
    @SerialName("access_token") val accessToken: String,
    @SerialName("refresh_token") val refreshToken: String,
    @SerialName("access_expires_at_ms") val accessExpiresAtMs: Long,
    @SerialName("user_id") val userId: String,
    /**
     * Registration RETURNS a session, so "has tokens" is not the same as "may use the app":
     * the contract requires a verified email before sending anything. Persisting the flag is
     * what lets a user who closes the app mid-signup come back to the code screen rather than
     * to a chat list that rejects their first message.
     */
    @SerialName("email_verified") val emailVerified: Boolean,
    /** Shown on the verification screen, so it survives a restart without a network call. */
    @SerialName("email") val email: String,
)

internal fun StoredSession.encodeForStorage(): String =
    ProtocolJson.encodeToString(StoredSession.serializer(), this)

internal fun decodeStoredSession(raw: String): StoredSession =
    ProtocolJson.decodeFromString(StoredSession.serializer(), raw)

/**
 * Credential storage, as an interface rather than a bare `expect class`.
 *
 * Two reasons, both concrete: an expect class cannot be substituted in `commonTest`, so the
 * refresh logic would be untestable; and swapping the backing store for
 * EncryptedSharedPreferences or the iOS Keychain becomes a change behind this boundary instead
 * of a change to every caller.
 */
interface SessionStorage {
    fun load(): StoredSession?
    fun save(session: StoredSession)
    fun clear()
}

/**
 * The platform implementation.
 *
 * Tokens are encrypted with an Android Keystore key and stored in the iOS Keychain. Platform
 * implementations migrate the previous private-preferences representation once, then remove it.
 */
expect class SessionStore : SessionStorage {
    /*
     * These three are redeclared here even though [SessionStorage] already declares them.
     *
     * An `expect class` that only INHERITS an interface satisfies the platform compilers -- each
     * one can see an actual that implements the members -- but `compileCommonMainKotlinMetadata`
     * has no actual to look at and rejects the class as "not abstract and does not implement
     * abstract members". Declaring them makes the expectation explicit, which is what that
     * compiler needs, and costs nothing at either platform.
     */
    override fun load(): StoredSession?
    override fun save(session: StoredSession)
    override fun clear()
}
